/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { LanguageCodeHelper } from '../../../shared/helpers/language-code-helper';
import { EditorContent, EditorContentRemovalEvent, EditorContentUpdateEvent, MediumEditorToolbarEvent } from '../../models';
import { EditorContentViewComponent } from '../editor-content-view/editor-content-view.component';
import { EditorToolbarComponent } from '../editor-toolbar/editor-toolbar.component';

@Component({
  selector: 'ptl-editor',
  templateUrl: './editor-main.component.html',
  styleUrls: ['./editor-main.component.scss'],
})
export class EditorMainComponent implements OnChanges, AfterViewInit {
  /** Receives the content */
  @Input() content: EditorContent[];
  /** Receives the placeholder text */
  @Input() placeholder = 'Add your text';
  /** edit editor options */
  @Input() editorOptions;

  @Input() disableAutoFocus = false;
  /** Enables url toolbar button */
  @Input() link: boolean;
  /** Enables upload toolbar button */
  @Input() upload: boolean;
  /** Enables embed code toolbar button */
  @Input() embed: boolean;
  /** Enables form toolbar button */
  @Input() form: boolean;
  /** Enables text form toolbar button */
  @Input() textFormOnly: boolean;
  /** Enables separator toolbar button */
  @Input() separator: boolean;
  /** Enables quiz toolbar button */
  @Input() quiz: boolean;
  /** Enables chart toolbar button */
  @Input() chart: boolean;
  /** Enables insert learnerCollectorSummary toolbar button */
  @Input() collector: boolean;
  /** Additional floating element id */
  @Input() floatingElementId: string;
  /** chart section uid */
  @Input() chartSectionUid: string;
  /** check if should show toolbar or not */
  @Input() showToolbar: boolean;

  @Input() emptyContentDisable: boolean;

  @Input() is360Report: boolean;

  @Input() toolbarEnabled = true;

  @Input() canRemoveItem = true;

  @Input() bytePlaceholder = false;
  /** Emits on content update. */
  @Output() contentUpdate = new EventEmitter<EditorContentUpdateEvent>();
  /** Emits on content removal. */
  @Output() contentRemoval = new EventEmitter<EditorContentRemovalEvent>();
  /** Emits data on new content from child components */
  @Output() contentCreated = new EventEmitter<EditorContent>();
  /** Outputs the various click and change events */
  @Output() inputFocus = new EventEmitter<void>();
  /** Outputs the various click and change events */
  @Output() inputBlur = new EventEmitter<void>();

  @ViewChild('editorToolbar', { static: false }) editorToolbar: EditorToolbarComponent;
  @ViewChild('editorContentView') editorContentView: EditorContentViewComponent;

  toolbarButtonVisible = false;
  toolbarOpen = false;
  toolbarButtonOffsetTop = 0;
  toolbarButtonOffsetLeft = 0;
  languageDirection = LanguageCodeHelper.getBodyLanguageDir();
  hasFloatingLinkBox = false;
  toolbarCalculatedWidth: number;

  private bubbleInsertElement: Element;
  private bubbleInsertContentUid: string;
  private toolbarButtonIndex: number;

  private TOOLBAR_LEFT_OFFSET_DELTA = -27;
  private BUTTON_PADDING = 20;
  private TOP_RADIUS = 20;
  private BOTTOM_RADIUS = 10;

  constructor(private elem: ElementRef) {
    this.toolbarButtonOffsetLeft = this.TOOLBAR_LEFT_OFFSET_DELTA;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.content) {
      this.ensureTrailingParagraph();
    }
  }

  ngAfterViewInit() {
    if (this.showToolbar) {
      this.toolbarButtonVisible = true;
    }
  }

  private ensureTrailingParagraph() {
    if (!this.toolbarEnabled) {
      return;
    }
    if (!(this.link || this.upload || this.embed || this.form || this.separator || this.quiz || this.chart || this.collector)) {
      return;
    }
    setTimeout(() => this.editorContentView?.ensureTrailingParagraph());
  }

  onContentRemoval(contentRemovalEvent: EditorContentRemovalEvent) {
    this.contentRemoval.emit(contentRemovalEvent);
  }

  onContentUpdate(contentUpdateEvent: EditorContentUpdateEvent) {
    this.contentUpdate.emit(contentUpdateEvent);
    this.editorToolbar?.setToolbarHidden();
  }

  onContentCreation(contentCreated: EditorContent) {
    if (contentCreated.index === undefined) {
      contentCreated.index = this.toolbarButtonIndex;
    }
    if (this.bubbleInsertElement) {
      this.editorContentView.bubbleInsertSplit(this.bubbleInsertElement, this.bubbleInsertContentUid);
      // Clear after use to prevent repeated splits from auto-generated paragraphs
      this.bubbleInsertElement = undefined;
      this.bubbleInsertContentUid = undefined;
    }
    this.contentCreated.emit(contentCreated);
    this.toolbarButtonVisible = false;
    this.toolbarOpen = false;
  }

  onContentUpload(contentUploaded: EditorContent) {
    this.contentCreated.emit(contentUploaded);
    this.toolbarButtonVisible = false;
    this.toolbarOpen = false;
  }

  onRegularLinkInsertion(link: string) {
    this.editorContentView.insertLinkBeforeBreakpoint(link);
    this.toolbarButtonVisible = false;
    this.toolbarOpen = false;
  }

  onToolbarUpdate(event: MediumEditorToolbarEvent) {
    // fire toolbar functionality only if at least one toolbar button present
    if (this.link || this.upload || this.form || this.separator || this.chart || this.quiz) {
      this.toolbarButtonVisible = event.type === 'SHOW_TOOLBAR';
      this.toolbarOpen = false;

      if (event.type === 'SHOW_TOOLBAR') {
        this.bubbleInsertElement = event.element;
        this.bubbleInsertContentUid = event.contentUid;
        this.toolbarButtonOffsetTop = event.offsetTop;
        this.toolbarButtonIndex = 0;
        this.toolbarCalculatedWidth = null;
        this.toolbarButtonOffsetLeft = this.TOOLBAR_LEFT_OFFSET_DELTA;

        if (this.toolbarButtonOffsetTop !== 0) {
          const contentItems = this.elem.nativeElement.querySelectorAll('.f_editor-element');

          for (const contentItem of contentItems) {
            this.setToolBarPosition(contentItem);

            if (this.toolbarButtonOffsetTop > contentItem.offsetTop) {
              ++this.toolbarButtonIndex;
            }
          }
        }

        const floatingElement = this.floatingElementId ? document.getElementById(this.floatingElementId) : undefined;

        if (floatingElement) {
          this.toolbarButtonOffsetLeft = this.TOOLBAR_LEFT_OFFSET_DELTA;
          const contentOffsetTop = floatingElement.offsetTop;
          const contentHeight = floatingElement.clientHeight;

          if (
            this.toolbarButtonOffsetTop >= contentOffsetTop - this.TOP_RADIUS &&
            this.toolbarButtonOffsetTop < contentOffsetTop + contentHeight + this.BOTTOM_RADIUS
          ) {
            this.toolbarButtonOffsetLeft = floatingElement.clientWidth + this.BUTTON_PADDING + this.TOOLBAR_LEFT_OFFSET_DELTA - 20;
          }
        }

        if (this.editorToolbar) {
          this.editorToolbar?.setToolbarHidden();
        }
      } else {
        this.bubbleInsertElement = undefined;
        this.bubbleInsertContentUid = undefined;
        this.toolbarButtonIndex = undefined;
      }
    }
  }

  onInputFocus() {
    this.inputFocus.emit();
  }

  onInputBlur() {
    this.inputBlur.emit();
  }

  private setToolBarPosition(contentItem: HTMLElement) {
    const leftPositionedElement: HTMLElement = contentItem.querySelector(
      '.content-media__html--position-LEFT, .content-media__html--position-THUMBNAIL,' +
        '.content-media-html--position-LEFT, .content-media-html--position-THUMBNAIL,' +
        '.embeded-code-preview-box-position-LEFT, .embeded-code-preview-box-position-THUMBNAIL',
    );

    const rightPositionedElement: HTMLElement = contentItem.querySelector(
      '.content-media__html--position-RIGHT, .content-media__html--position-THUMBNAIL-RIGHT,' +
        '.content-media-html--position-RIGHT, .content-media-html--position-THUMBNAIL-RIGHT,' +
        '.embeded-code-preview-box-position-RIGHT, .embeded-code-preview-box-position-THUMBNAIL-RIGHT',
    );

    if (leftPositionedElement || rightPositionedElement) {
      const positionedElement = leftPositionedElement ?? rightPositionedElement;
      const embedBoxWidth = positionedElement.clientWidth;
      const contentOffsetTop = positionedElement.offsetTop;
      const contentHeight = positionedElement.clientHeight;
      const contentWidth = contentItem.clientWidth;

      const toolbarWidth = contentWidth - embedBoxWidth - this.BUTTON_PADDING;

      const insidePositionedElementArea =
        this.toolbarButtonOffsetTop >= contentOffsetTop - this.TOP_RADIUS &&
        this.toolbarButtonOffsetTop < contentOffsetTop + contentHeight + this.BOTTOM_RADIUS;

      if (insidePositionedElementArea) {
        if (leftPositionedElement) {
          this.toolbarButtonOffsetLeft = embedBoxWidth + this.BUTTON_PADDING + this.TOOLBAR_LEFT_OFFSET_DELTA;
        } else {
          this.toolbarButtonOffsetLeft = -27;
        }

        this.toolbarCalculatedWidth = toolbarWidth;
      }
    }
  }
}
